import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/utils";
import { AlertCircle, CheckCircle } from "lucide-react";

// disabled:opacity-50
const inputVariants = cva(
  "flex w-full rounded-[15px] border bg-white px-[20px] py-[15px] text-[14px] font-[500] transition-all duration-200 file:border-0 file:bg-transparent outline-none focus-visible:outline-none file:text-sm file:font-medium disabled:text-[#A0AEC0] placeholder:text-[#A0AEC0] disabled:cursor-default",
  {
    variants: {
      variant: {
        default: "border-[#E2E8F0]",
        error: "border-[#ef4444]",
        success: "border-[#22c55e]",
      },
      size: {
        sm: "h-8",
        md: "h-10",
        lg: "h-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  label?: string;
  helperText?: string;
  errorMessage?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant,
      size,
      label,
      helperText,
      errorMessage,
      leftIcon,
      rightIcon,
      ...props
    },
    ref
  ) => {
    const hasError = !!errorMessage;
    const inputVariant = hasError ? "error" : variant;

    return (
      <div className="w-full flex flex-col space-y-2">
        {label && (
          <label className="text-[#2D3748] text-[16px] font-medium">
            {label}
            {props.required && <span className="text-[#ef4444] ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400">
              {leftIcon}
            </div>
          )}
          <input
            className={`${cn(
              inputVariants({ variant: inputVariant, className }),
              leftIcon && "pl-10",
              rightIcon && "pr-10",
              "h-[50px]",
              "text-[#1F1F1F]"
            )}`}
            ref={ref}
            {...props}
          />
          {rightIcon && !hasError && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400">
              {rightIcon}
            </div>
          )}
          {/* {hasError && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#ef4444]">
              <AlertCircle className="h-4 w-4" />
            </div>
          )} */}
          {variant === "success" && !hasError && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#22c55e]">
              <CheckCircle className="h-4 w-4" />
            </div>
          )}
        </div>
        {errorMessage && (
          <p className="text-xs text-[#ef4444] flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {errorMessage}
          </p>
        )}
        {helperText && !errorMessage && (
          <p className="text-xs text-neutral-500">{helperText}</p>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input, inputVariants };
